import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isPreviewMode } from '../previewMode';

const tenMinutesInMs = 10 * 1000 * 60;
const oneMinuteInMs = 1 * 1000 * 60;
const threeMinutesInMs = 3 * 1000 * 60;

/**
 *
 * @param callback
 * @param delay
 * @param heldBookingId
 */
export const useTimeout = (callback, delay = tenMinutesInMs, heldBookingId = null) => {
  const clear = useRef(() => {});
  const savedCallback = useRef(callback);
  const [timeRemaining, setTimeRemaining] = useState(delay);
  const [showExtender, setShowExtender] = useState(false);
  const [hasExtended, setHasExtended] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const intervalRef = useRef(null);
  const hasShownExtender = useRef(false);

  // Remember the latest callback if it changes.
  useLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const clearTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    clear.current();
  };

  const killTimer = () => {
    clearTimer();
    setTimeRemaining(0);
    setShowExtender(false);
    setHasExtended(true);
    hasShownExtender.current = true;
    setIsTimedOut(false);
  };

  const hideExtender = () => {
    setShowExtender(false);
    hasShownExtender.current = true;
  };

  const resetTimeout = () => {
    clearTimer();
    setTimeRemaining(delay);
    setShowExtender(false);
    setIsTimedOut(false);
    hasShownExtender.current = false;
    // console.log('Timer Reset - Starting with:', delay / 1000, 'seconds');

    const timerId = setTimeout(() => {
      setIsTimedOut(true);
      savedCallback.current();
    }, delay);
    clear.current = () => clearTimeout(timerId);

    // Start countdown interval
    intervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        const newTime = prev - 1000;

        // Log current time status
        // console.log('Time remaining:', newTime / 1000, 'seconds');

        // Only show extender if we haven't shown it before, not timed out, and other conditions are met
        if (
          newTime <= oneMinuteInMs &&
          !hasExtended &&
          !hasShownExtender.current &&
          !isTimedOut &&
          newTime > 0
        ) {
          setShowExtender(true);
        }

        // Stop interval when time runs out
        if (newTime <= 0) {
          // console.log('Timer finished!==>');
          clearInterval(intervalRef.current);
          setIsTimedOut(true);
          setShowExtender(false); // Close the extender when timing out
        }

        return newTime;
      });
    }, 1000);
  };

  const extendTime = () => {
    // Prevent extending if already timed out
    if (timeRemaining <= 0) {
      return;
    }
    clearTimer();
    const extensionTime = timeRemaining + threeMinutesInMs;
    // console.log('Extending timer by:', threeMinutesInMs / 1000, 'seconds');
    // console.log('New total time:', extensionTime / 1000, 'seconds');

    setTimeRemaining(extensionTime);
    setShowExtender(false);
    setHasExtended(true);
    hasShownExtender.current = true;

    const timerId = setTimeout(() => {
      setIsTimedOut(true);
      savedCallback.current();
    }, extensionTime);
    clear.current = () => clearTimeout(timerId);

    // Restart countdown interval
    intervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        const newTime = prev - 1000;
        // console.log('Extended time remaining:', newTime / 1000, 'seconds');

        if (newTime <= 0) {
          // console.log('Extended timer finished!');
          clearInterval(intervalRef.current);
          setIsTimedOut(true);
        }
        return newTime;
      });
    }, 1000);
  };

  // Set up the timeout
  useEffect(() => {
    if (!delay && delay !== 0) {
      clearTimer();
      return undefined;
    }

    if (isPreviewMode()) {
      return undefined;
    }

    resetTimeout();
    return clearTimer;
  }, [delay, heldBookingId]);

  if (isPreviewMode()) {
    return {
      clear: () => {},
      reset: () => {},
      extend: () => {},
      hideExtender: () => {},
      killTimer: () => {},
      timeRemaining: null,
      showExtender: false,
      hasExtended: false,
    };
  }

  return {
    clear: clearTimer,
    reset: resetTimeout,
    extend: extendTime,
    hideExtender,
    killTimer,
    timeRemaining,
    showExtender,
    hasExtended,
  };
};
